import { IFaceted, pack } from '@ms/items-view/lib/private/models/Facet';
import {
    createAddon,
    createSingleItemOperationHandler,
    IAddon,
    ISingleOperationPayload,
    IOperationHandlerThunkAction,
    registerOperationHandler,
} from '@ms/items-view/lib/private/addons/registerOperationHandler';
import { childItemsFacet, parentItemFacet } from '@ms/items-view/lib/private/facets/SetFacets';
import { getItemsOperation, supportFacets } from '@ms/items-view/lib/private/operations/getItems';
import { IItemCacheDispatch } from '@ms/items-view/lib/private/store/actions/ItemCacheActions';
import { IPatch, ArrayConcatIfNotMatchKeyPatch } from '@ms/items-view/lib/private/utilities/Graft';
import { displayNameFacet, fileNameFacet } from '@ms/items-view/lib/private/facets/ItemFacets';
import { ItemResolver } from '@ms/items-view/lib/private/models/items/ItemResolver';
import {
    resolveExistingPage,
    resolveNewPage,
} from '@ms/items-view/lib/private/models/items/PageHelpers';
import { blockItemPageKeyFacet } from '@ms/items-view/lib/private/operations/blockItemPage';
import getAttachmentItemsRequest from 'owa-service/lib/factory/getAttachmentItemsRequest';
import getAttachmentItemsOperation from 'owa-service/lib/operation/getAttachmentItemsOperation';
import indexedPageView from 'owa-service/lib/factory/indexedPageView';
import IndexedPageView from 'owa-service/lib/contract/IndexedPageView';
import { getHeaders } from 'owa-headers';
import {
    sentByFacet,
    subjectFacet,
    recievedDateFacet,
    mailboxKeyFacet,
    mailboxFileSetKeyFacet,
    mailboxFileSetPageKeyFacet,
    fileKeyFacet,
    fileGroupKeyFacet,
    mailboxFileGroupingSetKeyFacet,
    mailboxFileGroupingSetPageKeyFacet,
} from '../Schema/Facets';
import ModernAttachment from 'owa-service/lib/contract/ModernAttachment';
import { registerControlHandler, isGroupFacet } from '@ms/items-view';
import { itemThumbnailControl } from '@ms/items-view/lib/private/controls/itemThumbnailControl';
import { fileThumbnailControlHandler } from '../controls/itemThumbnailControl';
import GetAttachmentItemsResponse from 'owa-service/lib/contract/GetAttachmentItemsResponse';
import { lazyAddAndInitializeAttachments } from 'owa-attachment-model-store';
import { getRestrictionsFromAttachmentListFilterState } from '../utils/configureRestrictions';
import { getFilterStore } from './FilterStore';
import { getFilterString } from '../utils/getFilterString';
import { getParameterStore } from './ParametersStore';
import { getDisplayDateFromEwsDate, formatUserDate, formatYearMonth } from 'owa-datetime';
import { getSenderDisplayName } from '../utils/stringUtils';
import { PerformanceDatapoint, DatapointStatus } from 'owa-analytics';
import { MailboxType } from 'owa-client-ids';
import { getGroupMailboxExternalId } from 'owa-groups-shared-store';

const MAX_ENTRIES_RETURNED = 25;

export const filesAddon: IAddon = createAddon(() => {
    return (dispatch: IItemCacheDispatch) => {
        dispatch(
            registerOperationHandler(mailboxFileSetKeyFacet, getItemsOperation)(
                createSingleItemOperationHandler()(getMailboxPostsItem)
            )
        );

        dispatch(
            registerOperationHandler(mailboxFileSetPageKeyFacet, getItemsOperation)(
                createSingleItemOperationHandler()(getMailboxPostsPageItem)
            )
        );

        dispatch(
            registerOperationHandler(mailboxFileGroupingSetKeyFacet, getItemsOperation)(
                createSingleItemOperationHandler()(getGroupingMailboxPostsItem)
            )
        );

        dispatch(
            registerOperationHandler(mailboxFileGroupingSetPageKeyFacet, getItemsOperation)(
                createSingleItemOperationHandler()(getGroupingMailboxPostsPageItem)
            )
        );

        dispatch(
            registerControlHandler(fileKeyFacet, itemThumbnailControl)(fileThumbnailControlHandler)
        );

        dispatch(
            supportFacets({
                keyFacet: mailboxKeyFacet,
                facets: [],
            })
        );

        dispatch(
            supportFacets({
                keyFacet: mailboxFileSetKeyFacet,
                facets: [childItemsFacet],
            })
        );

        dispatch(
            supportFacets({
                keyFacet: fileKeyFacet,
                facets: [],
            })
        );

        dispatch(
            supportFacets({
                keyFacet: blockItemPageKeyFacet,
                facets: [],
            })
        );
    };
});

function getMailboxPostsItem(
    payload: ISingleOperationPayload<
        typeof mailboxFileSetKeyFacet.type,
        typeof getItemsOperation.type
    >
): IOperationHandlerThunkAction {
    const { itemKey } = payload;

    const { mailboxKey } = mailboxFileSetKeyFacet.deserialize(itemKey);

    return async (dispatch: IItemCacheDispatch) => {
        loadList(dispatch, itemKey, mailboxKey.smtp, 0, false);
    };
}

function getMailboxPostsPageItem(
    payload: ISingleOperationPayload<
        typeof mailboxFileSetPageKeyFacet.type,
        typeof getItemsOperation.type
    >
): IOperationHandlerThunkAction {
    const { itemKey } = payload;

    const { mailboxKey, itemSetKey, offset } = mailboxFileSetPageKeyFacet.deserialize(itemKey);

    return async (dispatch: IItemCacheDispatch) => {
        loadList(dispatch, itemKey, mailboxKey.smtp, offset, false, itemSetKey);
    };
}

function getGroupingMailboxPostsItem(
    payload: ISingleOperationPayload<
        typeof mailboxFileGroupingSetKeyFacet.type,
        typeof getItemsOperation.type
    >
): IOperationHandlerThunkAction {
    const { itemKey } = payload;

    const { mailboxFileSet } = mailboxFileGroupingSetKeyFacet.deserialize(itemKey);

    return async (dispatch: IItemCacheDispatch) => {
        loadList(dispatch, itemKey, mailboxFileSet.mailboxKey.smtp, 0, true);
    };
}

function getGroupingMailboxPostsPageItem(
    payload: ISingleOperationPayload<
        typeof mailboxFileGroupingSetPageKeyFacet.type,
        typeof getItemsOperation.type
    >
): IOperationHandlerThunkAction {
    const { itemKey } = payload;

    const { mailboxFileSetPage } = mailboxFileGroupingSetPageKeyFacet.deserialize(itemKey);

    return async (dispatch: IItemCacheDispatch) => {
        loadList(
            dispatch,
            itemKey,
            mailboxFileSetPage.mailboxKey.smtp,
            mailboxFileSetPage.offset,
            true,
            mailboxFileSetPage.itemSetKey
        );
    };
}

async function loadList(
    dispatch: IItemCacheDispatch,
    itemKey: string,
    smtp: string,
    offset: number,
    groupEnabled: boolean,
    itemSetKey?: string
) {
    const datapoint = new PerformanceDatapoint('FilesViewLoadList');
    datapoint.addCustomData({
        scenario: getParameterStore().scenarioName,
        offset: offset,
    });

    if (getParameterStore().mailboxInfo.type === MailboxType.GroupMailbox) {
        datapoint.addCustomData({
            groupExternalId: getGroupMailboxExternalId(
                getParameterStore().mailboxInfo &&
                    getParameterStore().mailboxInfo.primarySmtpAddress
            ),
        });
    }

    const response = await requestItems(smtp, offset, datapoint, groupEnabled);

    const itemResolver = await processItems(response, smtp, itemKey, groupEnabled, itemSetKey);

    dispatch(itemResolver.execute());

    datapoint.end();
}

async function requestItems(
    smtp: string,
    offset: number,
    datapoint: PerformanceDatapoint,
    groupEnabled: boolean
): Promise<GetAttachmentItemsResponse> {
    let columnSort = [0];
    if (!groupEnabled) {
        if (getFilterStore().columnSort.selectedValue[1]) {
            columnSort = [
                getFilterStore().columnSort.selectedValue[0] |
                    getFilterStore().columnSort.selectedValue[1],
            ];
        } else {
            columnSort = [getFilterStore().columnSort.selectedValue[0]];
        }
    }

    const requestBody = getAttachmentItemsRequest({
        Paging: indexedPageView(<IndexedPageView>{
            BasePoint: 'Beginning',
            Offset: offset,
            MaxEntriesReturned: MAX_ENTRIES_RETURNED,
        }),
        Restrictions: getRestrictionsFromAttachmentListFilterState(getFilterStore()),
        SortByColumns: columnSort,
    });

    const headers = getHeaders(
        smtp,
        `FilesView-${getParameterStore().scenarioName}${offset !== 0 ? '-scrolling' : ''}${
            getFilterString() !== '' ? '-filtered' : ''
        }`,
        true // adds canary to the headers
    );

    let response: GetAttachmentItemsResponse;
    try {
        response = await getAttachmentItemsOperation(requestBody, smtp && { headers }).then(
            response => {
                return response;
            }
        );
    } catch (error) {
        datapoint.endWithError(DatapointStatus.ServerError, error);
        throw error; // Bubble up the error so that items-view canc handle it and throw the appropriate error message
    }

    return response;
}

async function processItems(
    response: GetAttachmentItemsResponse,
    smtp: string,
    itemKey: string,
    groupEnabled: boolean,
    itemSetKey?: string
): Promise<ItemResolver> {
    const itemResolver = new ItemResolver();

    const fileKeys: string[] = [];

    let months = [];
    let monthsIndex = 0;

    const groupKeys: string[] = [];

    const mailboxKey = mailboxKeyFacet.serialize({ smtp });

    const addAndInitializeAttachmentModels = await lazyAddAndInitializeAttachments.import();

    for (let file of response.AttachmentItems) {
        const attachmentId = {
            Id: file.Data.Attachment.AttachmentId.Id,
            mailboxInfo: getParameterStore().mailboxInfo,
        };

        addAndInitializeAttachmentModels(
            [
                {
                    attachmentId,
                    attachment: file.Data.Attachment,
                },
            ],
            true /* isReadOnly */
        );

        const fileKey = fileKeyFacet.serialize({
            id: file.Data.Attachment.AttachmentId.Id,
            conversationId: file.Data.Item.ConversationId.Id,
            conversationSubject: file.Data.Item.Subject,
            sender: getSenderDisplayName(file.Info.Sender),
        });

        itemResolver.resolveItems({
            items: {
                [fileKey]: {
                    ...getFileFacets(file),
                },
            },
        });

        if (groupEnabled) {
            let dateString = formatYearMonth(
                getDisplayDateFromEwsDate(file.Data.Item.DateTimeReceived)
            );
            if (!months[monthsIndex]) {
                months.push({ date: dateString, items: [] });
            } else if (months[monthsIndex].date !== dateString) {
                months.push({ date: dateString, items: [] });
                monthsIndex++;
            }
            months[monthsIndex].items.push(fileKey);
        } else {
            fileKeys.push(fileKey);
        }
    }

    if (groupEnabled) {
        months.forEach(m => {
            if (m.items.length > 0) {
                let monthKey = fileGroupKeyFacet.serialize({
                    relativeTime: m.date.toString(),
                    itemSetKey: itemSetKey || itemKey,
                });
                groupKeys.push(monthKey);

                itemResolver.resolveItems({
                    items: {
                        [monthKey]: {
                            ...displayNameFacet.pack(m.date.toString()),
                            ...isGroupFacet.pack(true),
                            ...pack(
                                { childItems: childItemsFacet },
                                {
                                    childItems: {
                                        itemKeys: new ArrayConcatIfNotMatchKeyPatch(
                                            m.items,
                                            (i: string) => i
                                        ),
                                    },
                                }
                            ),
                        },
                    },
                });
            }
        });
    }

    if (response.IndexedPagingOffset < response.TotalAttachmentsInView) {
        if (groupEnabled) {
            const pageKey = mailboxFileGroupingSetPageKeyFacet.serialize({
                mailboxFileSetPage: {
                    mailboxKey: { smtp },
                    itemSetKey: itemSetKey || itemKey,
                    offset: response.IndexedPagingOffset,
                },
            });

            groupKeys.push(pageKey);

            itemResolver.merge(
                resolveNewPage({
                    itemKey: pageKey,
                }).itemResolver
            );
        } else {
            const pageKey = mailboxFileSetPageKeyFacet.serialize({
                mailboxKey: { smtp },
                itemSetKey: itemSetKey || itemKey,
                offset: response.IndexedPagingOffset,
            });

            fileKeys.push(pageKey);

            itemResolver.merge(
                resolveNewPage({
                    itemKey: pageKey,
                }).itemResolver
            );
        }
    }

    if (itemSetKey) {
        if (groupEnabled) {
            // Appends all new items and deduplicates previous existing groups
            itemResolver.resolveItems({
                items: {
                    [itemSetKey]: {
                        ...pack(
                            { childItems: childItemsFacet },
                            {
                                childItems: {
                                    itemKeys: new ArrayConcatIfNotMatchKeyPatch(groupKeys, i => i),
                                },
                            }
                        ),
                    },
                },
            });

            // Removes page just resolved from array
            itemResolver.merge(
                resolveExistingPage({
                    itemKey: itemKey,
                    itemSetKey: itemSetKey,
                    // Children have already been added during the deduplication step
                    childItemKeys: [],
                }).itemResolver
            );
        } else {
            itemResolver.merge(
                resolveExistingPage({
                    itemKey: itemKey,
                    itemSetKey: itemSetKey,
                    childItemKeys: fileKeys,
                }).itemResolver
            );
        }
    } else {
        itemResolver.resolveItems({
            items: {
                [itemKey]: {
                    ...childItemsFacet.pack({
                        itemKeys: groupEnabled ? groupKeys : fileKeys,
                    }),
                    ...parentItemFacet.pack({
                        itemKey: mailboxKey,
                    }),
                },
            },
        });
    }

    return itemResolver;
}

function getFileFacets(file: ModernAttachment): IPatch<IFaceted> {
    return {
        ...displayNameFacet.pack(file.Data.Attachment.Name),
        ...sentByFacet.pack(getSenderDisplayName(file.Info.Sender)),
        ...subjectFacet.pack(file.Data.Item.Subject),
        ...recievedDateFacet.pack(
            formatUserDate(getDisplayDateFromEwsDate(file.Data.Item.DateTimeReceived))
        ),
        ...getThumbnailFacets(file),
    };
}

function getThumbnailFacets(file: ModernAttachment): IPatch<IFaceted> {
    return {
        ...fileNameFacet.pack({
            name: `.${file.Data.AttachmentEx.FileName}`,
            extension: `.${file.Data.AttachmentEx.FileExtension}`,
        }),
    };
}
